import { isResponseError } from '@repo/shared-kit/utils';

export const NETWORK_ERROR_MESSAGES = new Set([
  // Chrome & Edge
  'Failed to fetch',
  // Firefox
  'NetworkError when attempting to fetch resource.',
  // Safari
  'cancelled',
  'annulé',
  'Abgebrochen',
  'Load failed',
  'La requête a expiré.',
  'La connexion Internet semble interrompue.',
  'La connexion réseau a été perdue.',
]);

/**
 * Returns the HTTP status code as a number, or `undefined` if this is not
 * an HTTP error.
 */
export function getHttpStatus(error: unknown): number | undefined {
  if (isFetchResponseError(error) || isResponseError(error)) {
    return error.status;
  }
}

/**
 * If an error should be sent to Sentry then this function will return `true`.
 * We return `false` for network issues, request abortions, HTTP server and unauthorized errors
 */
export function shouldSendToSentry(error: unknown): boolean {
  return (
    !isHttpServerError(error) &&
    !isAbortError(error) &&
    !isNetworkError(error) &&
    !(getHttpStatus(error) === 401)
  );
}

/**
 * If an HTTP request responds with a 5xx status code then this function will return `true`.
 */
function isHttpServerError(error: unknown): boolean {
  const httpStatus = getHttpStatus(error);
  return Boolean(httpStatus && httpStatus >= 500 && httpStatus < 600);
}

/**
 * If a network request is aborted then this function will return `true`.
 */
function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * If a network request fails due to network issues then this function will return `true`.
 */
function isNetworkError(error: unknown): boolean {
  return (
    (error instanceof TypeError && NETWORK_ERROR_MESSAGES.has(error.message)) ||
    (error instanceof DOMException && error.name === 'NetworkError') ||
    Boolean(
      error &&
        typeof error === 'object' &&
        'status' in error &&
        typeof error.status === 'number' &&
        error.status === 0
    )
  );
}

interface FetchResponseError extends Error {
  status: number;
  ok: boolean;
}

function isFetchResponseError(error: unknown): error is FetchResponseError {
  return Boolean(
    error &&
      typeof error === 'object' &&
      'status' in error &&
      typeof error.status === 'number' &&
      'ok' in error &&
      error.ok === false
  );
}

/*
 * Ember data extends the Error class with a `isAdapterError` property
 * and a `status` property.
 *
 * For now it's only checked in Ember Data calls, we need to rethink this when using it in React code
 */
type AdapterError = Error & { isAdapterError: boolean; status: number };

export const isNotFoundError = (error: unknown): error is AdapterError => {
  return (
    error instanceof Error &&
    (error as AdapterError).isAdapterError &&
    (error as AdapterError).status === 404
  );
};
